import type { MutableRefObject } from 'react';
import type { DisplayWindow, MenuDateField } from '../utils/displayWindow';

/**
 * What the header menu's Date range popup may do to the board.
 *
 * Bryntum's config is captured once, so the popup reaches SchedulerCore through
 * a stable ref rather than closures — the same reason `displayWindowRef` and
 * `timeZoneRef` exist.
 */
export type DateRangeMenuActions = {
  /** The window currently drawn, used to fill the fields. */
  getWindow: () => DisplayWindow;
  /** Apply one field edit; a rejected edit leaves the board where it was. */
  applyMenuEdit: (field: MenuDateField, value: Date) => void;
  shiftPrevious: () => void;
  shiftToday: () => void;
  shiftNext: () => void;
};

export type DateRangeMenuActionsRef = MutableRefObject<DateRangeMenuActions>;

/** The parts of a Bryntum DateField the popup touches. */
type DateFieldWidget = {
  value: unknown;
  suspendEvents: () => void;
  resumeEvents: () => void;
};

type PopupWidget = {
  widgetMap?: Record<string, DateFieldWidget | undefined>;
};

type WidgetEvent = {
  source?: { owner?: PopupWidget; up?: (type: string) => PopupWidget | null };
};

type FieldChangeEvent = WidgetEvent & {
  value: unknown;
  userAction?: boolean;
};

/**
 * Fill both fields from the drawn window without re-firing `change`.
 *
 * Skips quietly when either field is missing (popup not painted yet, or a
 * test stub) — a reset is cosmetic and must never throw.
 */
export function syncDateRangeFields(
  popup: PopupWidget | null | undefined,
  range: DisplayWindow
): void {
  const startField = popup?.widgetMap?.['startDateField'];
  const endField = popup?.widgetMap?.['endDateField'];
  if (!startField || !endField) return;

  startField.suspendEvents();
  endField.suspendEvents();
  startField.value = range.startDate;
  endField.value = range.endDate;
  startField.resumeEvents();
  endField.resumeEvents();
}

function popupOf(event: WidgetEvent): PopupWidget | null | undefined {
  return event.source?.up?.('popup') ?? event.source?.owner;
}

function asValidDate(value: unknown): Date | null {
  if (!(value instanceof Date)) return null;
  return Number.isNaN(value.getTime()) ? null : value;
}

function dateFieldConfig(
  field: MenuDateField,
  actionsRef: DateRangeMenuActionsRef
): Record<string, unknown> {
  return {
    type: 'datefield',
    label: field === 'start' ? 'Start date' : 'End date',
    weight: field === 'start' ? 310 : 320,
    labelWidth: '6em',
    required: true,
    // Keeps the field's back/forward step triggers, which Bryntum only
    // renders when `step` is set.
    step: '1d',
    highlightExternalChange: false,
    span: 3,
    listeners: {
      change(event: FieldChangeEvent) {
        // Programmatic writes (including syncDateRangeFields) are not edits.
        if (!event.userAction) return;
        const value = asValidDate(event.value);
        if (value) {
          actionsRef.current.applyMenuEdit(field, value);
        }
        // Applied, limited, rejected or cleared: the fields always end up
        // showing what the board actually draws.
        syncDateRangeFields(popupOf(event), actionsRef.current.getWindow());
      },
    },
  };
}

function shiftButtonConfig(
  weight: number,
  extra: Record<string, unknown>,
  onClick: () => void,
  actionsRef: DateRangeMenuActionsRef
): Record<string, unknown> {
  return {
    type: 'button',
    weight,
    ...extra,
    listeners: {
      click(event: WidgetEvent) {
        onClick();
        syncDateRangeFields(popupOf(event), actionsRef.current.getWindow());
      },
    },
  };
}

/**
 * Header-menu Date range item (ADO #109914).
 *
 * Replaces Bryntum's built-in `dateRange` item, which calls `setTimeSpan`
 * directly: that bypasses React — so the request never followed — and the
 * one-week clamp then restored the previous week straight away. Every edit here
 * goes through SchedulerCore's single apply path instead, the same one the
 * toolbar's prev/next/Today use. Layout, labels, icon and popup class mirror
 * the built-in item so the board looks unchanged.
 */
export function buildDateRangeMenuItem(
  actionsRef: DateRangeMenuActionsRef
): Record<string, unknown> {
  return {
    text: 'Date range',
    icon: 'b-fw-icon b-icon-calendar',
    weight: 300,
    menu: {
      type: 'popup',
      cls: 'b-sch-time-axis-menu-date-range-popup',
      items: {
        startDateField: dateFieldConfig('start', actionsRef),
        endDateField: dateFieldConfig('end', actionsRef),
        leftShiftBtn: shiftButtonConfig(
          330,
          { cls: 'b-left-nav-btn', icon: 'b-icon b-icon-previous' },
          () => actionsRef.current.shiftPrevious(),
          actionsRef
        ),
        todayBtn: shiftButtonConfig(
          340,
          { cls: 'b-today-nav-btn', text: 'Today', flex: 1 },
          () => actionsRef.current.shiftToday(),
          actionsRef
        ),
        rightShiftBtn: shiftButtonConfig(
          350,
          { cls: 'b-right-nav-btn', icon: 'b-icon b-icon-next' },
          () => actionsRef.current.shiftNext(),
          actionsRef
        ),
      },
      listeners: {
        // Reopening after toolbar navigation must not show a stale range.
        paint({ source }: { source?: PopupWidget }) {
          syncDateRangeFields(source, actionsRef.current.getWindow());
        },
      },
    },
  };
}
